import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { sumBy, isEmpty, debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Heading, Table, Card } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { riskValueUpdated, addInitialBranch, bulkBranchUpdate } from 'store/entities/activity-model/activity-model';
import { getRiskBranches } from 'store/entities/activity-model/selectors';
import { normalizeDecimalInputValue } from 'src/common/utils/decimal-input';
import { getResolver } from 'src/validation/resolver';
import { tasksSchemaValidator } from 'schemas/ajv-validators';
import { autoSaveWait } from 'src/config/config';
import { getBranchHeaderRow, getBranchRowsParams, getBranchUnitRow } from './branch-table-utils';

const BranchesTable = ({
  branches,
  addInitialBranch,
  isAdding,
  isPageDisabled,
  operationId,
  projectId,
  riskId,
  riskValueUpdated,
  estimateType,
  distributionType,
  onClickDeleteTask,
  debounceAddTask,
  bulkBranchUpdate,
}) => {
  const { t } = useTranslation();
  const debounceBulkBranchesUpdate = useRef(debounce(bulkBranchUpdate, autoSaveWait));
  const rowsParams = getBranchRowsParams({
    t,
    estimateType,
    distributionType,
  });
  const headerRow = getBranchHeaderRow(rowsParams);
  const unitRow = getBranchUnitRow(rowsParams);

  const actions = [
    {
      label: t(translations.add),
      disabled: isAdding || isPageDisabled,
      primary: true,
      icon: 'add',
      onClick: () => addInitialBranch(operationId, projectId, t(translations.activityModel_newBranch), riskId),
    },
  ];
  const [branchesErros, setBranchesErros] = useState([]);
  const resolver = useMemo(() => getResolver(tasksSchemaValidator), []);

  const validator = useCallback(
    async (branchesData, onSubmit, branchId) => {
      const certaintySum = sumBy(branchesData, 'certainty');
      const resolvedBranches = branchesData.map((branch) =>
        resolver({ ...branch, distribution: distributionType, estimateType }),
      );
      Promise.all(resolvedBranches)
        .then((res) => {
          if (certaintySum === 100) {
            return res.map((el) => el.errors);
          } else {
            return res.map((el) => ({
              ...el.errors,
              certainty: t(translations.totalValueOfProbabilityShouldBe, {
                sum: 100,
              }),
            }));
          }
        })
        .then((res) => {
          setBranchesErros(res);
          if (onSubmit) {
            const branchIndex = branchesData?.findIndex((branch) => branch.taskId === branchId);
            const isBrancheValid = branchIndex !== -1 && isEmpty(res[branchIndex]);
            if (isBrancheValid) {
              onSubmit();
            }
          }
        });
    },
    [distributionType, estimateType, resolver, t],
  );
  useEffect(() => {
    validator(branches);
  }, [branches, validator]);

  const tableRows = branches.map((branch, index) => {
    const errors = branchesErros?.[index];
    const cells = rowsParams.map(({ key }) => {
      const value = branch[key];

      return {
        type: key === 'name' ? 'Input' : 'NumberInput',
        allowEmpty: true,
        left: true,
        name: key,
        disabled: isPageDisabled,
        value,
        error: t(errors?.[key]),
        onChange: (e) => {
          const replaceComasToDotValue =
            key === 'name' ? e.target.value.replace(/,/, '.') : normalizeDecimalInputValue(e.target.value);

          riskValueUpdated({
            operationId,
            taskId: branch.taskId,
            value: replaceComasToDotValue,
            field: key,
          });
          const updatedBranch = {
            ...branch,
            operationId,
            projectId,
            [key]: replaceComasToDotValue,
          };
          const updatedIndex = branches.findIndex((b) => b.taskId === branch.taskId);
          const updatedBranches = branches.map((b) => ({
            ...b,
            operationId,
            projectId,
          }));
          updatedBranches[updatedIndex] = updatedBranch;
          const onSubmit = () =>
            key === 'certainty'
              ? debounceBulkBranchesUpdate.current({
                  activities: updatedBranches,
                  projectId,
                })
              : debounceAddTask.current(updatedBranch);
          validator(updatedBranches, onSubmit, branch.taskId);
        },
      };
    });

    return {
      cells,
      actions: [
        {
          label: t(translations.delete),
          icon: 'minus',
          onClick: () => onClickDeleteTask(branch),
          disabled: isPageDisabled,
        },
      ],
    };
  });

  const table = {
    bordered: false,
    headers: [{ cells: headerRow, actions }, { cells: unitRow }],
    rows: tableRows,
    footer: {},
  };

  return (
    <>
      <Card
        heading={
          <Heading>
            {t(translations.branches)} {branches.length ? ` (${branches.length})` : null}
          </Heading>
        }
        padding={0}
      >
        <Table table={table} />
      </Card>
    </>
  );
};

const mapStateToProps = ({ entities }, ownProps) => {
  const branches = getRiskBranches({ entities }, ownProps.operationId, ownProps.riskId);

  return { branches };
};

const mapDispatchToProps = {
  addInitialBranch,
  riskValueUpdated,
  bulkBranchUpdate,
};

const Container = connect(mapStateToProps, mapDispatchToProps)(BranchesTable);

export { Container as BranchesTable };
